import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import _ from "lodash";

import {
  _addClientRole,
  _deleteClientRole,
  _fetchClientRoleList,
  _updateClientRoles,
} from "@/services/clientusers";
import { defaultAllPermission } from "@/utilities/defaultData";
import { handleAPICatch, showSuccessToast } from "@/utilities/utils";

import { fetchUserAccessCounts } from "./userAccess";
import { formatRolesList } from "../../utilities/dataFormatter";

const initialState = {
  activeStep: 1,
  stepsValidated: {
    basic: true,
    moduleAccess: false,
    featureAccess: false,
  },

  editActiveStep: 1,
  editstepsValidated: {
    basic: true,
    moduleAccess: true,
    featureAccess: true,
  },
  isLoading: false,
  isPaginationLoading: false,
  roles: [],
  selectedRoles: [],
  filters: {
    search: "",
  },
  totalCount: 0,
  isSelectedAll: false,
  oldFilters: {},
  pagination: {
    currentPage: 1,
    totalPage: 1,
    isLastPage: false,
  },
  sorting: {
    sortByColumn: "",
    sortByOrder: "",
  },
  toggleSelected: false,
  showFilters: false,
  roleAccessData: {
    roleName: "",
    roleDescription: "",
    allPermission: defaultAllPermission,
  },
};

// fertch  client roles listing data
export const fetchClientRoles = createAsyncThunk(
  "roleAccess/fetchClientRoles",
  async (data, { dispatch, getState }) => {
    const {
      clientRoles: { filters, pagination, sorting, roles },
    } = getState();
    const currentRoleName = getState()?.clientProfile?.user?.role_name || "";
    if (_.isEmpty(data)) {
      data = {
        ...filters,
      };
    }
    if (pagination.isLastPage) {
      return dispatch(setClientRolesLoading(false));
    }

    dispatch(setPaginationLoading(pagination.currentPage !== 1));
    dispatch(toggleSelectedAll(false));
    const _data = {
      ...data,
      currentPage: pagination.currentPage,
      ...sorting,
    };

    const response = _fetchClientRoleList(_data);
    response
      .then((res) => {
        const {
          data: { user_roles_list, meta },
        } = res;
        const { currentPage, lastPage, isLastPage, total: totalCount } = meta;
        let data = formatRolesList(user_roles_list, currentRoleName);
        dispatch(
          updatePagination({
            currentPage: currentPage + 1,
            totalPage: lastPage,
            isLastPage: isLastPage,
          }),
        );
        let prevList = currentPage === 1 ? [] : _.uniq(roles);
        dispatch(updateTotalCount(totalCount));
        let first_roles_decr_role_id =
          data.length > 0 ? data[0].decr_role_id : null;
        if (
          prevList.length > 0 &&
          prevList.find(
            (item) => item.decr_role_id === first_roles_decr_role_id,
          ) !== undefined
        ) {
          //
        } else {
          dispatch(updateClientRoles([...prevList, ...data]));
        }
      })
      .catch((error) => {
        handleAPICatch(error);
      })
      .finally(() => {
        setTimeout(() => {
          dispatch(setClientRolesLoading(false));
          dispatch(setPaginationLoading(false));
        }, 10);
      });
  },
);

//delete client role list data
export const deleteClientRoleListdata = createAsyncThunk(
  "roleAccess/deleteClientRoleListdata",
  async (data, { dispatch }) => {
    const { callback, loading, setLoading, isSelectedAll, ignoreIds } = data;
    const _data = {
      role_id: data.role_id,
      isSelectedAll: isSelectedAll,
      ignoreIds: ignoreIds,
    };
    if (loading) return;
    setLoading(true);
    const response = _deleteClientRole(_data);
    response
      .then((res) => {
        const {
          data: { message },
        } = res;
        showSuccessToast(message);
        callback && callback();
        dispatch(deSelectAllRoles());
        dispatch(fetchUserAccessCounts());
      })
      .catch((error) => {
        handleAPICatch(error);
      })
      .finally(() => {
        setLoading(false);
      });
  },
);

// add client role form data
export const addRoleAccessdata = createAsyncThunk(
  "roleAccess/addRoleAccessdata",
  async ({ setSubmitting, callback = null }, { dispatch, getState }) => {
    const state = getState().clientRoles;
    let _postData = {
      ...state.roleAccessData,
    };
    setSubmitting(true);
    let response = _addClientRole(_postData);
    response
      .then((res) => {
        const {
          data: { message },
        } = res;
        dispatch(fetchUserAccessCounts());
        showSuccessToast(message);
        callback && callback();
      })
      .catch((error) => {
        handleAPICatch(error);
      })
      .finally(() => {
        setSubmitting(false);
      });
  },
);

// edit client role form data
export const editRoleAccessdata = createAsyncThunk(
  "roleAccess/editRoleAccessdata",
  async ({ setSubmitting, callback = null }, { dispatch, getState }) => {
    const state = getState().clientRoles;
    let _postData = {
      ...state.roleAccessData,
    };
    setSubmitting(true);
    let response = _updateClientRoles(_postData);
    response
      .then((res) => {
        const {
          data: { message },
        } = res;
        showSuccessToast(message);
        callback && callback();
      })
      .catch((error) => {
        handleAPICatch(error);
      })
      .finally(() => {
        setSubmitting(false);
      });
  },
);

const roleAccessSlice = createSlice({
  name: "roleAccess",
  initialState,
  reducers: {
    updateClientRoles: (state, action) => {
      state.roles = action.payload;
    },
    setClientRolesLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setPaginationLoading(state, action) {
      state.isPaginationLoading = action.payload;
    },
    updateSelectedClientRoles: (state, action) => {
      state.selectedRoles = action.payload;
    },
    updateFilters: (state, action) => {
      if (_.isEmpty(action.payload)) {
        state.filters = {
          search: state.filters.search,
          ...initialState.filters,
        };
        return;
      } else {
        if (action.payload.reset) {
          state.filters = initialState.filters;
        } else {
          state.filters = { ...state.filters, ...action.payload };
        }
      }
    },
    updateSorting: (state, action) => {
      state.sorting = action.payload;
    },
    deSelectAllRoles: (state) => {
      state.selectedRoles = [];
      state.isSelectedAll = false;
      state.toggleSelected = !state.toggleSelected;
    },
    showFilterModal: (state, action) => {
      if (action.payload) {
        state.oldFilters = state.filters;
      }
      state.showFilters = action.payload;
    },
    updatePagination: (state, action) => {
      state.pagination = action.payload;
    },
    reStateFilters: (state, action) => {
      state.filters = state.oldFilters;
    },
    updateToggleSelected: (state, action) => {
      state.toggleSelected = !state.toggleSelected;
    },
    updateTotalCount: (state, action) => {
      state.totalCount = action.payload;
    },
    toggleSelectedAll: (state, action) => {
      state.isSelectedAll = action.payload;
    },

    updateActiveStep: (state, action) => {
      state.activeStep = action.payload;
    },

    updateEditActiveStep: (state, action) => {
      state.editActiveStep = action.payload;
    },
    updateRoleAccessData: (state, action) => {
      state.roleAccessData = { ...state.roleAccessData, ...action.payload };
    },
    updatePermissions: (state, action) => {
      state.roleAccessData = {
        ...state.roleAccessData,
        allPermission: {
          ...state.roleAccessData.allPermission,
          ...action.payload,
        },
      };
    },
    updateStepsValidated: (state, action) => {
      state.stepsValidated = { ...state.stepsValidated, ...action.payload };
    },
    resetForm: (state, action) => {
      state.roleAccessData = initialState.roleAccessData;
      state.stepsValidated = initialState.stepsValidated;
      state.activeStep = 1;
    },
  },
});

export const {
  updateClientRoles,
  setClientRolesLoading,
  setPaginationLoading,
  updateSelectedClientRoles,
  updateFilters,
  updateSorting,
  deSelectAllRoles,
  showFilterModal,
  updatePagination,
  reStateFilters,
  updateToggleSelected,
  updateTotalCount,
  toggleSelectedAll,
  updateActiveStep,
  updateRoleAccessData,
  updateStepsValidated,
  resetForm,
  updatePermissions,
  updateEditActiveStep,
} = roleAccessSlice.actions;

export default roleAccessSlice.reducer;
